import { apiClient } from './axiosConfig';

// Messagerie de l'administration : la boîte commune du support (support@salatjanaza.org),
// ses brouillons, les nouveaux messages et les signatures. Routes sous /api/admin.

const BASE = '/api/admin';

const estFichier = (f) => typeof File !== 'undefined' && f instanceof File;

// ── Boîte et dossiers ─────────────────────────────────────────────────────────

/** { configuree, adresse, nom } */
export const getBoite = () => apiClient.get(`${BASE}/messagerie/boite`);

/** Une page d'un dossier : 'reception', 'envoyes' ou 'brouillons' — dix messages par page. */
export const getMessages = (dossier = 'reception', page = 1) =>
  apiClient.get(`${BASE}/messagerie`, { params: { dossier, page } });

/** Les pastilles des dossiers : { reception, nonLus, envoyes, brouillons, erreur }. */
export const getCompteursMessagerie = () => apiClient.get(`${BASE}/messagerie/compteurs`);

/** Supprime des messages (corbeille de la boîte) ou des brouillons. */
export const supprimerMessages = (identifiants, dossier) =>
  apiClient.post(`${BASE}/messagerie/supprimer`, { identifiants }, { params: { dossier } });

/**
 * Un message avec son corps (marqué lu au passage). `images` reste à faux par défaut :
 * les images distantes d'un courriel servent surtout à savoir s'il a été ouvert.
 */
export const getMessage = (id, images = false, dossier = 'reception') =>
  apiClient.get(`${BASE}/messagerie/${id}`, { params: { images, dossier } });

export const marquerMessageLu = (id, lu, dossier = 'reception') =>
  apiClient.post(`${BASE}/messagerie/${id}/lu`, null, { params: { lu, dossier } });

/** Une pièce jointe d'un message, dans son dossier. */
export const telechargerPieceJointe = (id, nom, dossier = 'reception') =>
  apiClient.get(`${BASE}/messagerie/${id}/piece`, {
    params: { nom, dossier },
    responseType: 'blob',
  });

/** Répond dans le fil, depuis la boîte du support, avec la signature de l'administrateur. */
export const repondreMessage = (id, texte, { cc = [], cci = [] } = {}) =>
  apiClient.post(`${BASE}/messagerie/${id}/repondre`, { texte, cc, cci });

// ── Adresses proposées ────────────────────────────────────────────────────────

/** Les adresses de l'équipe (les administrateurs) : [{ nom, mail }]. */
export const getContactsEquipe = () => apiClient.get(`${BASE}/messagerie/contacts`);

/** Les utilisateurs dont le nom ou l'adresse correspond : [{ nom, mail }] (dix au plus). */
export const rechercherDestinataires = (q) =>
  apiClient.get(`${BASE}/messagerie/destinataires`, { params: { q } });

// ── Brouillons ────────────────────────────────────────────────────────────────

/** Le brouillon de réponse à ce message : 404 s'il n'en a pas. */
export const getBrouillonPour = (messageOrigine) =>
  apiClient.get(`${BASE}/messagerie/brouillons/${messageOrigine}`);

/**
 * Enregistre un brouillon ; un brouillon vide s'efface (204). Une réponse porte
 * `messageOrigine` ; un message libre porte son `id` une fois créé, et son `titre`.
 */
export const enregistrerBrouillon = (brouillon) =>
  apiClient.put(`${BASE}/messagerie/brouillons`, brouillon);

/** Un brouillon libre (« Écrire un nouveau message »), avec la liste de ses pièces. */
export const getBrouillonLibre = (id) =>
  apiClient.get(`${BASE}/messagerie/brouillons/libre/${id}`);

/** Les pièces d'un brouillon libre, toutes remplacées d'un coup, dans l'ordre (l'ordre fait les [image:N]). */
export const remplacerPiecesBrouillon = (id, images, documents) => {
  const corps = new FormData();
  (images ?? []).filter(estFichier).forEach((f) => corps.append('images', f, f.name));
  (documents ?? []).filter(estFichier).forEach((f) => corps.append('documents', f, f.name));
  return apiClient.put(`${BASE}/messagerie/brouillons/libre/${id}/pieces`, corps);
};

/** Une pièce d'un brouillon libre, pour la remettre dans le formulaire. */
export const getPieceBrouillon = (id, pieceId) =>
  apiClient.get(`${BASE}/messagerie/brouillons/libre/${id}/pieces/${pieceId}`, { responseType: 'blob' });

// ── Nouveau message ───────────────────────────────────────────────────────────

/**
 * Le corps multipart d'un nouveau message, partagé par l'aperçu et l'envoi : deux
 * constructions séparées finiraient par diverger, et l'aperçu montrerait autre chose
 * que ce qui part. L'ordre des images fait la référence des marqueurs [image:N].
 */
function corpsMessage({ destinataire, sujet, titre, texte, images, documents, cc, cci, brouillonId }) {
  const corps = new FormData();
  corps.append('destinataire', destinataire ?? '');
  corps.append('sujet', sujet ?? '');
  corps.append('titre', titre ?? '');
  corps.append('texte', texte ?? '');
  (images ?? []).filter(estFichier).forEach((f) => corps.append('images', f, f.name));
  (documents ?? []).filter(estFichier).forEach((f) => corps.append('documents', f, f.name));
  (cc ?? []).forEach((a) => corps.append('cc', a));
  (cci ?? []).forEach((a) => corps.append('cci', a));
  if (brouillonId) corps.append('brouillonId', String(brouillonId));
  return corps;
}

/** Le message rendu en HTML, avec la signature de l'administrateur connecté. */
export const apercuNouveauMessage = (composition) =>
  apiClient.post(`${BASE}/mails/apercu`, corpsMessage(composition), { responseType: 'text' });

/** Envoie le message depuis la boîte du support ; son brouillon libre (`brouillonId`) est effacé. */
export const envoyerNouveauMessage = (composition) =>
  apiClient.post(`${BASE}/mails/nouveau`, corpsMessage(composition));

// ── Signatures (super-administrateur) ─────────────────────────────────────────

export const getSignatures = () => apiClient.get(`${BASE}/signatures`);
export const creerSignature = (saisie) => apiClient.post(`${BASE}/signatures`, saisie);
export const modifierSignature = (id, saisie) => apiClient.put(`${BASE}/signatures/${id}`, saisie);
export const supprimerSignature = (id) => apiClient.delete(`${BASE}/signatures/${id}`);
export const apercuSignature = (saisie) =>
  apiClient.post(`${BASE}/signatures/apercu`, saisie, { responseType: 'text' });

/** `signatureId` null : le modèle par défaut. */
export const attribuerSignature = (utilisateurId, signatureId) =>
  apiClient.put(`${BASE}/utilisateurs/${utilisateurId}/signature`, { signatureId });

/** La signature d'un compte, telle que ses destinataires la verront. */
export const apercuSignatureCompte = (utilisateurId) =>
  apiClient.get(`${BASE}/utilisateurs/${utilisateurId}/signature/apercu`, { responseType: 'text' });

// ── Événements en direct ──────────────────────────────────────────────────────

/** L'adresse du flux SSE des événements admin (lu avec fetch : EventSource ne porte pas d'en-tête). */
export const URL_EVENEMENTS = `${process.env.REACT_APP_API_URL ?? ''}${BASE}/evenements`;
